import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { SwitchingBetweenApps } from "@tailormed/common-client/component/SwitchingBetweenApps";
import {
  CoreSideNav,
  LogoButton,
  ModuleSelector,
  ModuleSelectorLeft,
  ProductIcon,
  ProductLabel
} from "@tailormed/common-client/component/AppShell";
import { TmLogoLockup } from "@tailormed/common-client/assets/svg/TmLogoLockup";
import { TmArchesMark } from "@tailormed/common-client/assets/svg/TmArchesMark";
import { IconNavHelp as ContactSupportIcon } from "@tailormed/common-client/assets/svg/IconNavHelp";

import { ReactComponent as ArrowDownIcon } from "../assets/svg/arrow-down-icon.svg";
import { history } from "../router/history";
import { selectMenuItems, selectUser } from "../store/selector";
import ACTIONS from "../store/action";
import { Routes } from "../constant/routes";
import { MENU_ICONS } from "../constant/menu";
import { useGetSetting } from "../hooks/getSetting";

const { Text } = Typography;
const SITE_NAME = "insights";

const SideBar = () => {
  const { t } = useTranslation();
  const { location } = history;
  const { pathname } = location;
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const sideBarMenus = useSelector(selectMenuItems) || [];

  const [collapsed, setCollapsed] = useState(false);
  const [selectedSubMenuKey, setSelectedSubMenuKey] = useState([]);

  useEffect(() => {
    const pathNames = pathname.split("/").filter((i) => i && i !== SITE_NAME);
    setSelectedSubMenuKey(pathNames.length ? pathNames.slice(-1)[0] : []);
  }, [location]);

  const goToHomePage = () => history.push({ pathname: Routes.ROOT, state: undefined });
  const handleContactSupport = () => dispatch(ACTIONS.setShowContactSupportModal(true));
  const navigateTo = (title, url) =>
    history.push({ pathname: Routes.ROOT + Routes.SELECT + title, state: { url, title } });

  const moduleSlot = (
    <SwitchingBetweenApps user={user} goToHomePage={goToHomePage} useGetSetting={useGetSetting} pathname={pathname}>
      {({ hasModulesToSwitch }) => (
        <LogoButton
          type="text"
          id="financial_insights"
          collapsed={collapsed}
          onClick={() => {
            if (!hasModulesToSwitch) {
              goToHomePage();
            }
          }}
        >
          <ModuleSelector collapsed={collapsed}>
            <ModuleSelectorLeft>
              <ProductIcon module="insights" size={24} />
              {!collapsed && <ProductLabel>{t("financial_insights")}</ProductLabel>}
            </ModuleSelectorLeft>
            {hasModulesToSwitch && <ArrowDownIcon />}
          </ModuleSelector>
        </LogoButton>
      )}
    </SwitchingBetweenApps>
  );

  const navGroups = (sideBarMenus.MenuItems ?? []).map((menu) => ({
    key: menu.key,
    id: menu.key,
    icon: MENU_ICONS[menu.key],
    label: <Text>{menu.title}</Text>,
    collapsedLabel: menu.title,
    onClick: () => {
      if (menu?.subMenus?.[0]) {
        navigateTo(menu.subMenus[0].title, menu.subMenus[0].url);
      }
    },
    subItems: (menu.subMenus ?? []).map((subMenu, index) => ({
      key: subMenu.title,
      id: `${menu.key}_${index}_sub_menu`,
      label: <Text>{subMenu.title}</Text>,
      onClick: () => navigateTo(subMenu.title, subMenu.url)
    }))
  }));

  const footerItems = [
    {
      key: "contact_support",
      id: "contact_support_btn",
      icon: <ContactSupportIcon />,
      label: t("sidebar_menu.contact_support"),
      tooltipPlacement: "topLeft",
      onClick: handleContactSupport
    }
  ];

  return (
    <CoreSideNav
      collapsed={collapsed}
      onToggleCollapse={() => setCollapsed(!collapsed)}
      brand={{
        logoFull: <TmLogoLockup />,
        logoMark: <TmArchesMark />,
        onLogoClick: goToHomePage,
        logoId: "tailormed_logo"
      }}
      moduleSlot={moduleSlot}
      navGroups={navGroups}
      footerItems={footerItems}
      copyright={t("footer", { year: new Date().getFullYear() })}
      navMenuId="side_menu"
      selectedKeys={selectedSubMenuKey ? [].concat(selectedSubMenuKey) : []}
    />
  );
};

export { SideBar };
